import { useState } from 'react';
import { Outlet, NavLink, Navigate, useLocation } from 'react-router-dom';
import { brand } from '@frontdesk/config';
import { useAuth } from '../lib/auth';

interface NavItem {
  to: string;
  label: string;
  icon: string;
  end?: boolean;
}

const primaryNav: NavItem[] = [
  { to: '/app', label: 'Home', icon: '⌂', end: true },
  { to: '/app/contacts', label: 'Contacts', icon: '◉' },
  { to: '/app/calendar', label: 'Calendar', icon: '▦' },
  { to: '/app/inbox', label: 'Messages', icon: '✉' },
  { to: '/app/settings', label: 'Settings', icon: '⚙' },
];

const moreNavGroups: { label: string; items: NavItem[] }[] = [
  {
    label: 'Sales',
    items: [
      { to: '/app/pipelines', label: 'Pipelines', icon: '◳' },
      { to: '/app/invoices', label: 'Invoices', icon: '$' },
      { to: '/app/products', label: 'Products', icon: '◧' },
      { to: '/app/services', label: 'Services', icon: '◮' },
    ],
  },
  {
    label: 'Work',
    items: [
      { to: '/app/tasks', label: 'Tasks', icon: '✓' },
      { to: '/app/projects', label: 'Projects', icon: '◰' },
      { to: '/app/workflows', label: 'Workflows', icon: '◫' },
    ],
  },
  {
    label: 'Marketing',
    items: [
      { to: '/app/marketing', label: 'Campaigns', icon: '◐' },
      { to: '/app/reputation', label: 'Reputation', icon: '★' },
      { to: '/app/social', label: 'Social', icon: '◓' },
      { to: '/app/pages', label: 'Pages', icon: '▤' },
      { to: '/app/forms', label: 'Forms', icon: '▥' },
      { to: '/app/blog', label: 'Blog', icon: '▦' },
    ],
  },
  {
    label: 'Learn',
    items: [
      { to: '/app/courses', label: 'Courses', icon: '◭' },
      { to: '/app/memberships', label: 'Memberships', icon: '◈' },
      { to: '/app/community', label: 'Community', icon: '◔' },
    ],
  },
  {
    label: 'Communication',
    items: [
      { to: '/app/voice', label: 'Voice', icon: '☎' },
      { to: '/app/esignature', label: 'eSignature', icon: '✎' },
    ],
  },
  {
    label: 'Insights',
    items: [
      { to: '/app/reports', label: 'Reports', icon: '▣' },
    ],
  },
  {
    label: 'Admin',
    items: [
      { to: '/app/whitelabel', label: 'White-label', icon: '◧' },
    ],
  },
];

export function AppLayout() {
  const user = useAuth((s) => s.user);
  const tenantId = useAuth((s) => s.tenantId);
  const [moreOpen, setMoreOpen] = useState(false);
  const location = useLocation();

  if (!tenantId || !user) {
    return <Navigate to="/auth/login" replace />;
  }

  const allMoreItems = moreNavGroups.flatMap((g) => g.items);
  const currentInMore = allMoreItems.some((i) => location.pathname.startsWith(i.to));

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--color-surface-secondary)' }}>
      <nav
        style={{
          width: '240px',
          background: 'var(--color-surface-primary)',
          borderRight: '1px solid var(--color-border-primary)',
          padding: 'var(--space-4)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-1)',
          position: 'sticky',
          top: 0,
          height: '100vh',
          overflowY: 'auto',
        }}
      >
        <div
          style={{
            fontSize: 'var(--text-xl)',
            fontWeight: 700,
            padding: 'var(--space-4) var(--space-2)',
            color: 'var(--color-brand-primary)',
          }}
        >
          {brand.appName}
        </div>
        {primaryNav.map((item) => (
          <NavLink key={item.to} to={item.to} end={item.end} style={navLinkStyle}>
            <span>{item.icon}</span>
            {item.label}
          </NavLink>
        ))}
        <button
          onClick={() => setMoreOpen((v) => !v)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            padding: 'var(--space-2) var(--space-3)',
            borderRadius: 'var(--radius-lg)',
            color: currentInMore ? 'var(--color-brand-primary)' : 'var(--color-text-secondary)',
            background: currentInMore ? 'var(--color-brand-primary-light)' : 'transparent',
            fontWeight: currentInMore ? 600 : 400,
            fontSize: 'var(--text-sm)',
            border: 'none',
            cursor: 'pointer',
            textAlign: 'left',
          }}
        >
          <span>{moreOpen ? '▾' : '▸'}</span>
          More
        </button>
        {moreOpen && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', marginTop: 'var(--space-2)' }}>
            {moreNavGroups.map((group) => (
              <div key={group.label}>
                <div
                  style={{
                    fontSize: 'var(--text-xs)',
                    fontWeight: 600,
                    color: 'var(--color-text-tertiary)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    padding: '0 var(--space-3)',
                    marginBottom: 'var(--space-1)',
                  }}
                >
                  {group.label}
                </div>
                {group.items.map((item) => (
                  <NavLink key={item.to} to={item.to} style={navLinkStyle}>
                    <span>{item.icon}</span>
                    {item.label}
                  </NavLink>
                ))}
              </div>
            ))}
          </div>
        )}
        <div
          style={{
            marginTop: 'auto',
            padding: 'var(--space-2) var(--space-3)',
            fontSize: 'var(--text-xs)',
            color: 'var(--color-text-tertiary)',
          }}
        >
          {user.email}
        </div>
      </nav>
      <main style={{ flex: 1, overflow: 'auto', padding: 'var(--space-6) var(--space-8)' }}>
        <Outlet />
      </main>
    </div>
  );
}

const navLinkStyle = ({ isActive }: { isActive: boolean }): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  padding: 'var(--space-2) var(--space-3)',
  borderRadius: 'var(--radius-lg)',
  color: isActive ? 'var(--color-brand-primary)' : 'var(--color-text-secondary)',
  background: isActive ? 'var(--color-brand-primary-light)' : 'transparent',
  fontWeight: isActive ? 600 : 400,
  fontSize: 'var(--text-sm)',
  textDecoration: 'none',
  transition: 'background var(--duration-fast) var(--ease-default)',
});
